import { Card, CardColors, Separator, Text } from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'
import { formatCurrencyFromCents } from '@/utils/currencyHelpers'
import { DATE_FORMATS_LUXON, isoToUTCDateTime } from '@/utils/dateHelpers'
import type {
  SafeHarborCatchUp,
  SafeHarborCatchUpQuarter,
} from '@/features/taxes/quarterly/safeHarborCatchUp.slice'

const money = (cents: number) =>
  formatCurrencyFromCents(cents, { hideDecimalsIfZero: true })

const Row = ({
  label,
  value,
  muted = false,
}: {
  label: string
  value: string
  muted?: boolean
}) => (
  <div className="flex flex-row justify-between items-baseline gap-4">
    <Text
      variant={TypographyStyles.BODY_MD}
      color={muted ? TextColors.MUTED : undefined}
    >
      {label}
    </Text>
    <Text variant={TypographyStyles.BODY_MD}>{value}</Text>
  </div>
)

const QuarterCard = ({ quarter }: { quarter: SafeHarborCatchUpQuarter }) => {
  const { quarterNumber, dueDate, perQuarterInCents, paidInCents } = quarter
  const isPaid = paidInCents > 0
  const outstandingInCents = Math.max(0, perQuarterInCents - paidInCents)

  return (
    <div className="rounded-lg bg-neutral-50 p-3 space-y-1">
      <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
        {dueDate
          ? `Q${quarterNumber} · due ${isoToUTCDateTime(dueDate).toFormat(
              DATE_FORMATS_LUXON.DISPLAY_SHORT
            )}`
          : `Q${quarterNumber}`}
      </Text>
      <Text variant={TypographyStyles.BODY_MD}>
        {money(outstandingInCents)}
      </Text>
      {isPaid && (
        <Text variant={TypographyStyles.BODY_SM} color={TextColors.MOSS}>
          {money(paidInCents)} paid
        </Text>
      )}
    </div>
  )
}

/**
 * Leads with what to pay now, then lays the covered quarters out. The member's
 * instinct is that one quarter is the bill, so the multiplication is shown
 * rather than asserted.
 */
export const CatchUpSummary = ({ catchUp }: { catchUp: SafeHarborCatchUp }) => {
  const {
    perQuarterInCents,
    quartersCovered,
    paymentsLoggedInCents,
    totalDueNowInCents,
    quarters,
  } = catchUp

  const hasPayments = paymentsLoggedInCents > 0
  const isSingleQuarter = quartersCovered === 1

  return (
    <Card color={CardColors.EMPHASIS} className="w-full text-left">
      <div className="space-y-4">
        <div>
          <Text variant={TypographyStyles.EYEBROW} color={TextColors.MUTED}>
            federal
          </Text>
          <Text variant={TypographyStyles.DISPLAY_SM}>
            {money(totalDueNowInCents)}
          </Text>
          <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
            Total due now
          </Text>
        </div>

        <Separator />

        <div className="space-y-2">
          <Row label="Per quarter" value={money(perQuarterInCents)} />
          <Row label="Quarters covered" value={String(quartersCovered)} />
          {hasPayments && (
            <Row
              muted
              label="Already paid"
              value={`-${money(paymentsLoggedInCents)}`}
            />
          )}
        </div>

        {!isSingleQuarter && (
          <>
            <Separator />
            <div className="space-y-2">
              <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
                What this covers
              </Text>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {quarters.map((quarter) => (
                  <QuarterCard key={quarter.taxQuarter} quarter={quarter} />
                ))}
              </div>
            </div>
          </>
        )}

        <Text variant={TypographyStyles.BODY_SM} color={TextColors.MOSS}>
          This is paid as one payment for the current quarter. The IRS cannot
          accept a payment toward a past quarter, so catching up means paying
          the full amount now.
        </Text>
      </div>
    </Card>
  )
}
